import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Bot, Play, Square, Clock, Globe, CheckCircle, XCircle, Loader2,
  RefreshCw, Trash2, Zap, AlertTriangle, Settings2, FileText, Search
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AutoPublisher() {
  const [status, setStatus] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [sites, setSites] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [schedule, setSchedule] = useState({work_hours_start: 23, work_hours_end: 7});
  const [editSchedule, setEditSchedule] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [siteSearch, setSiteSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, configsRes, sitesRes, logsRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/auto-publish/status`),
        axios.get(`${API}/auto-publish/config`),
        axios.get(`${API}/sites?status=active`),
        axios.get(`${API}/auto-publish/logs?limit=20`),
        axios.get(`${API}/auto-publish/schedule`),
      ]);
      setStatus(statusRes.data);
      setConfigs(configsRes.data);
      setSites(Array.isArray(sitesRes.data) ? sitesRes.data : sitesRes.data?.sites || []);
      setLogs(logsRes.data);
      if (scheduleRes?.data) setSchedule(scheduleRes.data);
      else if (statusRes?.data?.work_hours_start !== undefined) setSchedule({work_hours_start: statusRes.data.work_hours_start, work_hours_end: statusRes.data.work_hours_end});
      try { const gs = await axios.get(`${API}/google/status`); setGoogleStatus(gs.data); } catch(e) {}
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getConfigForSite = (siteId) => {
    return configs.find(c => c.site_id === siteId) || {
      site_id: siteId, enabled: false, interval_days: 1, support_count: 3, ai_model: "grok"
    };
  };

  const handleToggle = async (siteId, enabled) => {
    setSaving(siteId);
    try {
      const config = getConfigForSite(siteId);
      await axios.put(`${API}/auto-publish/config/${siteId}`, {
        ...config, enabled, interval_days: config.interval_days || 1,
        support_count: config.support_count ?? 3, ai_model: config.ai_model || "grok"
      });
      toast.success(enabled ? "Auto-publish włączony" : "Auto-publish wyłączony");
      fetchData();
    } catch (err) { toast.error("Błąd zapisu"); }
    finally { setSaving(null); }
  };

  const handleUpdateConfig = async (siteId, field, value) => {
    setSaving(siteId);
    try {
      const config = getConfigForSite(siteId);
      await axios.put(`${API}/auto-publish/config/${siteId}`, { ...config, [field]: value });
      toast.success("Zapisano");
      fetchData();
    } catch (err) { toast.error("Błąd"); }
    finally { setSaving(null); }
  };

  const handleRunNow = async (siteId) => {
    try {
      await axios.post(`${API}/auto-publish/run-now/${siteId}`);
      toast.success("Zadanie uruchomione w tle!");
      setTimeout(fetchData, 3000);
    } catch (err) { toast.error("Błąd uruchamiania"); }
  };

  const handleConnectGoogle = async () => {
    try { const res = await axios.get(`${API}/google/auth-url`); window.open(res.data.url, "_blank", "width=600,height=700"); } catch(e) { toast.error("Blad"); }
  };

  const handleSaveSchedule = async () => {
    try {
      await axios.put(`${API}/auto-publish/schedule`, schedule);
      toast.success("Godziny pracy zapisane");
      setEditSchedule(false);
      fetchData();
    } catch (err) { toast.error("Blad zapisu"); }
  };

  const handleStartAgent = async () => {
    try {
      await axios.post(`${API}/auto-publish/start`);
      toast.success("Agent uruchomiony");
      fetchData();
    } catch (err) { toast.error("Błąd"); }
  };

  const handleStopAgent = async () => {
    try {
      await axios.post(`${API}/auto-publish/stop`);
      toast.success("Agent zatrzymany");
      fetchData();
    } catch (err) { toast.error("Błąd"); }
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Wyczyścić wszystkie logi?")) return;
    try {
      await axios.delete(`${API}/auto-publish/logs`);
      toast.success("Logi wyczyszczone");
      fetchData();
    } catch (err) { toast.error("Błąd"); }
  };

  const enabledSites = sites.filter(s => getConfigForSite(s.id).enabled);
  const disabledSites = sites.filter(s => !getConfigForSite(s.id).enabled);
  const filteredDisabled = disabledSites.filter(s => 
    !siteSearch || s.name?.toLowerCase().includes(siteSearch.toLowerCase()) || s.url?.toLowerCase().includes(siteSearch.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="auto-publisher-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Auto Publisher</h1>
          <p className="text-muted-foreground text-sm mt-1">Automatyczna publikacja artykułów SEO z artykułami wspierającymi</p>
        </div>
      <ModuleDescription title="Jak dziala Auto Publisher?">
        <p><strong>Auto Publisher</strong> to automatyczny agent ktory 24/7 generuje i publikuje artykuly SEO na Twoich stronach WordPress i Emergent.</p>
        <p>Kazda strona ma swoja konfiguracje: interwal publikacji (co ile godzin/dni), ile artykulow wspierajacych, jaki model AI (Grok/GPT-4o/Gemini).</p>
        <p><strong>Przyciski:</strong> "Teraz" = natychmiastowa publikacja na stronie | "Nowa runda" = wymus nowy cykl | "Zatrzymaj agenta" = pauza</p>
        <p><strong>Logi po prawej</strong> pokazuja ostatnie publikacje z linkami. Czerwone = bledy (agent ponowi je automatycznie po 24h).</p>
        <p><strong>Google News / Discover</strong> = checkboxy informujace czy strona jest w Google News i Discover (sprawdzane automatycznie).</p>
      </ModuleDescription>

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Odśwież
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try { await axios.post(`${API}/sites/check-google-news-discover`); toast.success("Sprawdzanie Google News/Discover uruchomione w tle!"); } catch(e) { toast.error("Blad"); }
          }} className="gap-1.5 text-green-600 border-green-200">
            <Globe className="w-3.5 h-3.5" /> Sprawdz GN/GD
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            if (window.confirm("Zresetować interwały? Agent zacznie nową rundę od zera.")) {
              try { await axios.post(`${API}/auto-publish/force-new-round`); toast.success("Nowa runda! Agent zacznie od nowa."); fetchData(); } catch(e) { toast.error("Błąd"); }
            }
          }} className="gap-1.5 text-amber-600 border-amber-200">
            <Zap className="w-3.5 h-3.5" /> Nowa runda
          </Button>
          {status?.running ? (
            <Button variant="destructive" size="sm" onClick={handleStopAgent} className="gap-1.5">
              <Square className="w-3.5 h-3.5" /> Zatrzymaj agenta
            </Button>
          ) : (
            <Button size="sm" onClick={handleStartAgent} className="gap-1.5 bg-green-600 hover:bg-green-700">
              <Play className="w-3.5 h-3.5" /> Uruchom agenta
            </Button>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-4 text-center">
          <div className={`inline-flex items-center gap-1.5 text-sm font-medium ${status?.running ? "text-green-600" : "text-red-500"}`}>
            <div className={`w-2 h-2 rounded-full ${status?.running ? "bg-green-500 animate-pulse" : "bg-red-400"}`} />
            {status?.running ? "Aktywny" : "Zatrzymany"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Agent</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className={`text-sm font-medium ${status?.work_hours ? "text-green-600" : "text-amber-600"}`}>
            <Clock className="w-3.5 h-3.5 inline mr-1" />
            {status?.work_hours ? "Pracuje" : "Poza godzinami"}
          </div>
          {editSchedule ? (
            <div className="flex items-center gap-1 mt-1 justify-center">
              <select value={schedule.work_hours_start} onChange={e => setSchedule(s => ({...s, work_hours_start: parseInt(e.target.value)}))}
                className="h-6 text-xs border rounded px-1 w-14">
                {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
              </select>
              <span className="text-xs">-</span>
              <select value={schedule.work_hours_end} onChange={e => setSchedule(s => ({...s, work_hours_end: parseInt(e.target.value)}))}
                className="h-6 text-xs border rounded px-1 w-14">
                {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
              </select>
              <Button size="sm" className="h-6 text-[10px] px-2" onClick={handleSaveSchedule}>OK</Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-1 cursor-pointer hover:text-primary" onClick={() => setEditSchedule(true)}>
              {status?.work_schedule} <Settings2 className="w-2.5 h-2.5 inline" />
            </p>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-lg font-bold text-primary">{status?.enabled_sites || 0}</p>
          <p className="text-xs text-muted-foreground">Włączonych stron</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-lg font-bold text-green-600">{status?.total_published || 0}</p>
          <p className="text-xs text-muted-foreground">Opublikowanych</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-lg font-bold text-red-500">{status?.total_errors || 0}</p>
          <p className="text-xs text-muted-foreground">Błędów</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center cursor-pointer" onClick={!googleStatus?.connected ? handleConnectGoogle : undefined}>
          <div className={`text-sm font-medium ${googleStatus?.connected ? "text-green-600" : "text-gray-400"}`}>
            <Globe className="w-3.5 h-3.5 inline mr-1" />
            {googleStatus?.connected ? "Google" : "Polacz Google"}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{googleStatus?.connected ? "Search Console OK" : "Kliknij"}</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Strony z włączonym auto-publish */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="w-4 h-4" /> Strony z auto-publish ({enabledSites.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto max-h-[500px]">
              <div className="space-y-3 p-6 pt-3">
              {enabledSites.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Brak włączonych stron. Włącz auto-publish dla wybranej strony poniżej.</p>
              )}
              {enabledSites.map(site => {
                const config = getConfigForSite(site.id);
                return (
                  <div key={site.id} className="border rounded-lg p-3 space-y-2" data-testid={`site-config-${site.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary" />
                        <span className="font-medium text-sm">{site.name}</span>
                        <Badge variant="outline" className="text-[10px]">{site.category || "?"}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleRunNow(site.id)}>
                          <Zap className="w-3 h-3" /> Teraz
                        </Button>
                        <Switch
                          checked={config.enabled}
                          onCheckedChange={(v) => handleToggle(site.id, v)}
                          disabled={saving === site.id}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Interwał:</span>
                        <Select value={String(config.interval_days)} onValueChange={(v) => handleUpdateConfig(site.id, "interval_days", parseInt(v))}>
                          <SelectTrigger className="h-6 w-24 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Co 1 dzień</SelectItem>
                            <SelectItem value="2">Co 2 dni</SelectItem>
                            <SelectItem value="3">Co 3 dni</SelectItem>
                            <SelectItem value="5">Co 5 dni</SelectItem>
                            <SelectItem value="7">Co 7 dni</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Wspierające:</span>
                        <Select value={String(config.support_count)} onValueChange={(v) => handleUpdateConfig(site.id, "support_count", parseInt(v))}>
                          <SelectTrigger className="h-6 w-16 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[0,1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Model:</span>
                        <Select value={config.ai_model || "grok"} onValueChange={(v) => handleUpdateConfig(site.id, "ai_model", v)}>
                          <SelectTrigger className="h-6 w-20 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="grok">Grok</SelectItem>
                            <SelectItem value="gemini">Gemini</SelectItem>
                            <SelectItem value="claude">Claude</SelectItem>
                            <SelectItem value="gpt">GPT</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer" title="Google News">
                        <input type="checkbox" className="w-3 h-3 rounded"
                          checked={site.google_news || false}
                          onChange={async (e) => { try { await axios.put(`${API}/sites/${site.id}/google-news-status`, {google_news: e.target.checked}); fetchData(); } catch(err) {} }} />
                        <span className="text-green-700 font-medium">Google News</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer" title="Strona newsowa (Breaking News)">
                        <input type="checkbox" className="w-3 h-3 rounded"
                          checked={site.news_site || false}
                          onChange={async (e) => { try { await axios.put(`${API}/sites/${site.id}/google-news-status`, {news_site: e.target.checked}); fetchData(); } catch(err) {} }} />
                        <span className="text-amber-700 font-medium">News</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer" title="Google Discover">
                        <input type="checkbox" className="w-3 h-3 rounded"
                          checked={site.google_discover || false}
                          onChange={async (e) => { try { await axios.put(`${API}/sites/${site.id}/google-news-status`, {google_discover: e.target.checked}); fetchData(); } catch(err) {} }} />
                        <span className="text-blue-700 font-medium">Google Discover</span>
                      </label>
                    </div>
                    {config.last_error && (
                      <div className="flex items-center gap-1 text-xs text-red-500">
                        <AlertTriangle className="w-3 h-3" /> {config.last_error.substring(0, 80)}
                      </div>
                    )}
                    {config.last_run && (
                      <p className="text-[10px] text-muted-foreground">Ostatnie: {new Date(config.last_run).toLocaleString("pl-PL")}</p>
                    )}
                  </div>
                );
              })}
              </div>
              </div>
            </CardContent>
          </Card>

          {/* Dostępne strony */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings2 className="w-4 h-4" /> Dostępne strony ({disabledSites.length})
                </CardTitle>
                <span className="text-xs text-muted-foreground">{filteredDisabled.length} wyników</span>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Szukaj strony..."
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  className="h-8 text-xs pl-8"
                  data-testid="site-search-input"
                />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="h-[400px]">
                <div className="space-y-1">
                  {filteredDisabled.map(site => (
                    <div key={site.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-xs truncate">{site.name}</span>
                        <Badge variant="secondary" className="text-[9px] shrink-0">{site.site_type || "wp"}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {site.google_news && <Badge className="text-[8px] bg-green-100 text-green-700 px-1">GN</Badge>}
                        {site.google_discover && <Badge className="text-[8px] bg-blue-100 text-blue-700 px-1">GD</Badge>}
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggle(site.id, true)}
                          disabled={saving === site.id}
                        />
                      </div>
                    </div>
                  ))}
                  {filteredDisabled.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">Brak wyników dla "{siteSearch}"</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Logi */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Logi ({logs.length})
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={handleClearLogs} className="h-6 text-xs gap-1 text-muted-foreground">
                  <Trash2 className="w-3 h-3" /> Wyczyść
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {logs.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8">Brak logów</p>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log, idx) => (
                      <div key={log.id || idx} className={`border rounded-lg p-2.5 text-xs ${
                        log.status === "completed" ? "border-green-200 bg-green-50/50 dark:bg-green-950/20" :
                        log.status === "error" ? "border-red-200 bg-red-50/50 dark:bg-red-950/20" :
                        "border-blue-200 bg-blue-50/50 dark:bg-blue-950/20"
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{log.site_name || "?"}</span>
                          {log.status === "completed" ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> :
                           log.status === "error" ? <XCircle className="w-3.5 h-3.5 text-red-500" /> :
                           <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
                        </div>
                        {log.title && <p className="text-muted-foreground truncate">{log.title}</p>}
                        {log.main_url && <a href={log.main_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline truncate block">{log.main_url}</a>}
                        {log.support_published !== undefined && (
                          <p className="text-muted-foreground">Wspierające: {log.support_published}/{log.support_total}</p>
                        )}
                        {log.error && <p className="text-red-500 truncate">{log.error}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {log.started_at && new Date(log.started_at).toLocaleString("pl-PL")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
